import { Paperclip } from 'lucide-react';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import { applicantsApi } from '../applicantsApi';
import type { ApplicantAssessment, ApplicantAssessmentAttachment } from '../applicantTypes';

/**
 * One assessment stage's tab — ONE card, ONE Edit, over the stage's one form: Result, Remarks (the
 * stored document, rendered through the one rich-text surface), Attachments (each downloads), and who
 * last recorded it. An unrecorded stage shows its empty state and the way to fill it.
 *
 * The tab of a HEALTH stage is gated on the medical key, so a hidden stage never reaches here.
 */
export function ApplicantAssessmentPanel({
  applicantId,
  stageLabel,
  assessment,
  editDisabledReason,
  qualified,
  onEdit,
}: {
  applicantId: string;
  stageLabel: string;
  /** Null while the stages load. */
  assessment: ApplicantAssessment | null;
  editDisabledReason: string | null;
  /** Screened Qualified — the one state in which a stage can be recorded at all. */
  qualified: boolean;
  onEdit: () => void;
}) {
  const toast = useToast();

  async function download(file: ApplicantAssessmentAttachment) {
    if (!assessment) return;

    try {
      const { blob, fileName } = await applicantsApi.downloadAssessmentFile(applicantId, assessment.stage, file.id);
      saveBlob(blob, fileName ?? file.fileName);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the file.');
    }
  }

  const recorded = assessment?.result != null;

  return (
    <ProfileCard
      id={`applicant-profile-assessment-${assessment?.stage ?? 'loading'}`}
      title={stageLabel}
      onEdit={assessment ? onEdit : undefined}
      editPermission="applicants.update"
      editLabel={recorded ? 'Edit' : `Record ${stageLabel.toLowerCase()}`}
      editDisabledReason={editDisabledReason}
      empty={
        assessment && !recorded
          ? {
              title: 'Not recorded yet.',
              // The Initial Interview's own wording: say what opens it, not only what it holds.
              description: qualified
                ? `Record the ${stageLabel.toLowerCase()} result, the remarks and its documents.`
                : `The ${stageLabel.toLowerCase()} is recorded once the applicant is screened Qualified. Record the initial screening first.`,
            }
          : undefined
      }
    >
      {assessment ? (
        <PreviewTable variant="table-display" rows={rows(assessment, (file) => void download(file))} />
      ) : (
        <Skeleton className="h-40 w-full" />
      )}
    </ProfileCard>
  );
}

/** One fact per row; every row renders whatever the value, an absent one as an em dash. */
function rows(assessment: ApplicantAssessment, onDownload: (file: ApplicantAssessmentAttachment) => void): PreviewRow[] {
  return [
    { label: 'Result', value: assessment.resultLabel },
    {
      label: 'Remarks',
      value: assessment.remarks ? <RichTextDocument html={assessment.remarks} /> : null,
    },
    {
      label: 'Attachments',
      value:
        assessment.attachments.length > 0 ? (
          <ul className="flex min-w-0 flex-col gap-1">
            {assessment.attachments.map((file) => (
              <li key={file.id} className="min-w-0">
                <FileLink fileName={file.fileName} onDownload={() => onDownload(file)} />
              </li>
            ))}
          </ul>
        ) : null,
    },
    { label: 'Updated by', value: assessment.updatedByName },
    {
      label: 'Updated',
      value: assessment.updatedAt != null ? formatDateTime(assessment.updatedAt) : null,
      mono: true,
    },
  ];
}

/** A filed document's name, which DOWNLOADS it — the paperclip on its left, as on the checklist. */
function FileLink({ fileName, onDownload }: { fileName: string; onDownload: () => void }) {
  return (
    <Tooltip label={`Download ${fileName}`} block>
      <button
        type="button"
        onClick={onDownload}
        className="inline-flex min-w-0 max-w-full items-center gap-1 text-sm font-medium text-slate-800 hover:underline focus:outline-none focus-visible:underline"
      >
        <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 truncate">{fileName}</span>
        <span className="sr-only">, download</span>
      </button>
    </Tooltip>
  );
}
